import React, {useEffect} from 'react';
import {Permission} from "@src/Interfaces/Entity/Permission";
import {useFormik} from "formik";
import {State} from "@src/Interfaces/Entity/Person";
import {FormControlLabel, Grid, Switch} from "@material-ui/core";
import {FullWidthTextField} from "@src/Shared/styles";
import {Values} from "Tools/Values";
import TransferList from "@src/Components/Common/TransferList/TransferList";
import * as Yup from "yup";
import { IUserRole } from '@src/Stores/Applications/types';

const validationSchema = (uniquenessValidator: () => any) => {
    return Yup.object().shape({
        name: Yup.string().required(() => {
            return Values.getLocalizationValue("fieldRequired");
        })
            .test(
                "isNameExists",
                Values.getLocalizationValue("nameAlreadyExists"),
                uniquenessValidator()
            )
    });
};

interface IPermissionDialogFormProps {
    setRef: (ref: any) => void;
    initialValues: Permission;
    onSubmit: (data: any) => void;
    uniquenessValidator?: () => void;
    allRoles: IUserRole[];
    selectedRoles: IUserRole[];
}

const PermissionDialogForm = (props: IPermissionDialogFormProps) => {
    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
            state: State.Active,
            roles: []
        },
        onSubmit: (data) => submit(data),
        validationSchema: validationSchema(props.uniquenessValidator),
        validateOnChange: false,
        validateOnBlur: false
    });

    const submit = (data: any) => {
        let rolesForSave: IUserRole[] = [];
        data.roles.forEach((role: any) => {
            let foundRoleObj: IUserRole = props.allRoles.find(x => x.id == role);
            rolesForSave.push(foundRoleObj)
        });

        data.roles = rolesForSave;
        props.onSubmit(data)
    }

    useEffect(() => {
        if (props.initialValues) {
            formik.setValues({...props.initialValues});
        }
    }, [props.initialValues]);

    const onStateChange = (checked: boolean) => {
        const state = checked ? State.Active : State.Deactivated;
        formik.setFieldValue("state", state);
    }
    
    return (
        <form onSubmit={formik.handleSubmit} ref={ref => props.setRef(ref)}>
            <Grid container spacing={2} direction={"column"}>
                <Grid item>
                    <FullWidthTextField
                        name={"name"}
                        onChange={formik.handleChange}
                        value={formik.values.name}
                        size={"small"}
                        error={formik.errors.name}
                        helperText={formik.errors.name}
                        label={Values.getLocalizationValue("name")}
                        variant="outlined"
                    />
                </Grid>

                <Grid item>
                    <FullWidthTextField
                        name={"description"}
                        onChange={formik.handleChange}
                        value={formik.values.description}
                        size={"small"}
                        error={formik.errors.description}
                        helperText={formik.errors.description}
                        label={Values.getLocalizationValue("description")}
                        variant="outlined"
                        multiline
                        rows={4}
                    />
                </Grid>

                <Grid item>
                    <TransferList
                        fieldName="roles"
                        label={Values.getLocalizationValue("roles")}
                        formikProps={formik}
                        allItems={props.allRoles || []}
                        selectedItems={props.selectedRoles || []}
                        keyAsValue="id"
                        keyAsTitle="displayTitle"
                    />
                </Grid>

                <Grid item>
                    <FormControlLabel
                        label={Values.getLocalizationValue("active")}
                        control={
                            <Switch
                                color={"primary"}
                                name={"state"}
                                onChange={(ev) => onStateChange(ev.target.checked)}
                                checked={formik.values.state === State.Active}
                            />
                        }
                    />
                </Grid>
            </Grid>
        </form>
    );
};

export default PermissionDialogForm;